import { useState, type FormEvent } from 'react'
import { useCreateTag, useDeleteTag, useRenameTag, useTags, type Tag } from '../api/tags'
import { useMe } from '../api/me'
import { RequestFailed } from '../api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

// The tenant's managed tag list. Owners and admins change it; the server
// refuses everyone else whatever this page shows.
export function TagsPage() {
  const me = useMe()
  const tags = useTags()
  const create = useCreateTag()
  const [name, setName] = useState('')
  const admin = me.data?.user.role === 'owner' || me.data?.user.role === 'admin'

  const submit = (e: FormEvent) => {
    e.preventDefault()
    create.mutate(name.trim(), { onSuccess: () => setName('') })
  }

  return (
    <main className="mx-auto flex max-w-2xl flex-col gap-6 px-4 py-6 sm:py-8">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">Tags</h1>
        <p className="text-sm text-muted-foreground">
          Tags group contacts so the inbox can filter them and campaigns can target them later. Names ignore capital letters.
        </p>
      </div>
      {admin && (
        <form className="flex flex-col gap-2" onSubmit={submit}>
          <Label htmlFor="tag-name">New tag</Label>
          <div className="flex gap-2">
            <Input id="tag-name" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} />
            <Button type="submit" disabled={!name.trim() || create.isPending}>
              {create.isPending ? 'Adding' : 'Add'}
            </Button>
          </div>
          {create.error && <p role="alert" className="text-sm text-destructive">{(create.error as RequestFailed).message}</p>}
        </form>
      )}
      {tags.isPending ? (
        <div role="status" aria-label="Loading tags" className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : tags.isError ? (
        <p role="alert" className="rounded-lg bg-destructive-soft p-3 text-sm text-destructive">{(tags.error as RequestFailed).message}</p>
      ) : tags.data.length === 0 ? (
        <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          No tags yet. {admin ? 'Add the first one above.' : 'An owner or admin can add them.'}
        </p>
      ) : (
        <ul className="divide-y rounded-xl border bg-card">
          {tags.data.map((t) => (
            <TagRow key={t.id} tag={t} admin={admin} />
          ))}
        </ul>
      )}
    </main>
  )
}

function TagRow({ tag, admin }: { tag: Tag; admin: boolean }) {
  const rename = useRenameTag()
  const del = useDeleteTag()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(tag.name)
  const [confirm, setConfirm] = useState(false)
  const error = (rename.error ?? del.error) as RequestFailed | null

  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      {editing ? (
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            rename.mutate({ id: tag.id, name: name.trim() }, { onSuccess: () => setEditing(false) })
          }}
        >
          <Input aria-label={`New name for ${tag.name}`} maxLength={40} value={name} onChange={(e) => setName(e.target.value)} autoFocus className="min-w-0 flex-1" />
          <Button type="submit" disabled={!name.trim() || rename.isPending}>Save</Button>
          <Button type="button" variant="ghost" onClick={() => { setEditing(false); setName(tag.name) }}>Cancel</Button>
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="text-sm">{tag.name}</Badge>
          <span className="flex-1" />
          {admin && !confirm && (
            <>
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>Rename</Button>
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setConfirm(true)}>Delete</Button>
            </>
          )}
          {admin && confirm && (
            <>
              <span className="text-sm text-muted-foreground">Delete and remove from every contact?</span>
              <Button variant="destructive" size="sm" disabled={del.isPending} onClick={() => del.mutate(tag.id)}>Delete</Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirm(false)}>Keep</Button>
            </>
          )}
        </div>
      )}
      {error && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
    </li>
  )
}
